/**
 * How long an editor has left to confirm ("Mark as working") an edit that was routed back to them.
 *
 * Derived from the absolute `deadline_at` instant the API already sends, never from the
 * `hours_remaining` scalar beside it. That scalar is computed server-side at fetch time, and the
 * prompt's query fetches once per full page load (`staleTime` 60s, no refetch on focus, no
 * interval, and the component is mounted app-wide so it never remounts). While the deadline was
 * Studio's 24h backstop a figure that stale was invisible; now that `deadline_at` is Scale Coach's
 * projected release — hours, not a day — a frozen number is materially wrong. An editor who leaves
 * the tab open reads the figure from whenever the page loaded, right up to the moment the item is
 * released (SM-9828).
 *
 * `deadline_at` is the same instant the release acts on, so rendering against it is what keeps the
 * countdown and the backend from drifting.
 */
export interface AckCountdown {
    /** The deadline has passed. The item can return to Ready to Edit at any time. */
    overdue: boolean
    /** Whole hours left. 0 inside the final hour. */
    hours: number
    /** Whole minutes left inside the final hour (1-59). Only meaningful when `hours` is 0. */
    minutes: number
}

/**
 * @param deadlineIso Absolute ISO-8601 instant, or null when the release is switched off.
 * @param now         Milliseconds since the epoch — passed in so the caller owns the clock and the
 *                    value is a reactive dependency rather than a hidden `Date.now()` read.
 */
export function ackCountdown(deadlineIso: string | null | undefined, now: number): AckCountdown | null {
    if (!deadlineIso) return null

    const deadline = new Date(deadlineIso).getTime()
    if (Number.isNaN(deadline)) return null

    // Rounded up so the final seconds read "1m left" rather than "0m left": the item is still the
    // editor's until the sweep runs, and a zero is easily read as "already gone".
    const minutesLeft = Math.ceil((deadline - now) / 60_000)

    if (minutesLeft <= 0) {
        return { overdue: true, hours: 0, minutes: 0 }
    }

    return {
        overdue: false,
        hours: Math.floor(minutesLeft / 60),
        minutes: minutesLeft % 60,
    }
}
